import React, { useState, useEffect } from "react";
import styles from "../Details/Details.module.css";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import PopUp from "../Popup/Popup";
import axios from "axios";
import { API_BASE_URL, getUserDetails } from "../../Store/apiStore";
import decodeToken from "../../lib/decodeToken";
import useUser from "../../Store/Context/UserContext";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { parsePhoneNumberFromString, getCountryCallingCode } from "libphonenumber-js";
import { useRef } from "react";
import AnimatedButton from "../AnimatedButton/AnimatedButton";
import useOnboardingTracker from "../../hooks/useOnboardingTracker";
import useDetailsNavigation from "../../hooks/useDetailsNavigation";
import useSimPhoneNumberAutofill from "../../hooks/useSimPhoneNumberAutofill";
import { trackEvent } from "../../lib/analytics";
import { isIndiaSite } from "../../utils/apiBackend";
import { resolveHomeRoute } from "../../utils/resolveHomeRoute";

/*
 * Details — owner Personal Details capture (name + phone + optional
 * email + SMS opt-in). Used in two modes:
 *
 *   - Standalone (default) — full-page component on /personal-details,
 *     navigates to /steps or /business-step after save (legacy flow).
 *
 *   - Wizard mode (PostPurchaseSetup Step 2) — `wizardMode={true}`
 *     suppresses the standalone navigation + onboardComplete localStorage
 *     write, and instead calls `onWizardNext()` so the wizard shell
 *     drives the next step. Optional `onWizardSkip` is reserved for
 *     future opt-out pathways; not wired today.
 */
const Details = ({ wizardMode = false, onWizardNext, onWizardSkip } = {}) => {
  // console.log(
  //   "[Details DEBUG] mounted — wizardMode:",
  //   wizardMode,acking event personal_details_entered
  //   "url:",
  //   window.location.href,
  // );
  const [searchParams] = useSearchParams();
  const [startExit, setStartExit] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState(null);
  const [popupMessage, setPopupMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");           // ← email state
  const [emailError, setEmailError] = useState(""); // ← email error state
  const [nameError, setNameError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [nameSubmitted, setNameSubmitted] = useState(false);
  const [phoneSubmitted, setPhoneSubmitted] = useState(false);
  // Toggle by default ON — naye user ka payload smsConsent: true ke saath jaata
  // hai. Jo user pehle se save kar chuka hai uski apni value niche wale effect
  // se restore hoti hai (userDetails.smsConsent), ye sirf starting value hai.
  const [smsConsent, setSmsConsent] = useState(true);
  const token = localStorage.getItem("token");
  const decodeTokenData = decodeToken(token);
  const userId = decodeTokenData?.id;
  const { user, setUser, updateUser } = useUser();
  // Starting country for the phone field. On the .in site the caller is
  // Indian, so opening on +1 means every user has to change it by hand; the
  // .ai site keeps +1. This is only the STARTING value — a prefilled number
  // (applyPhone) or the country dropdown still override it, same as before.
  const [country, setCountry] = useState(isIndiaSite() ? "in" : "us");
  const referralCode = sessionStorage.getItem("referredBy") || "";
  const referredByName = sessionStorage.getItem("referredByName") || "";
  const listRef = useRef(null);
  const phoneInputRef = useRef(null);
  // GA4: track whether the name / phone "entered" events already fired via the
  // field blur (manual typing). If the value was PREFILLED (Google/JWT login),
  // the field is never focused → blur never fires, so Continue fires them as a
  // safety net. Refs (not the GA guard alone) so we can tag source typed/prefilled.
  const nameEnteredFiredRef = useRef(false);
  const phoneEnteredFiredRef = useRef(false);
  const isIphone = /iPhone|iPod/i.test(navigator.userAgent);
  const { steps, trackStep } = useOnboardingTracker();
  // Post-save navigation lives in a hook so this screen carries no routing
  // branches — after details are saved we just call goToNextStep().
  const { goToNextStep } = useDetailsNavigation();
  const [userDetails, setUserDetails] = useState();

  const navigate = useNavigate();
  // Details already submitted (Continue succeeded). Coming back to /details
  // (Back button / repeated clicks) must not show the form again — send the
  // user to /fast-agent-detail instead. A per-user flag is used, NOT
  // onboardComplete: SecureRoute sets that to "true" for any ?token= link, so
  // an app reopen before Continue would wrongly skip this screen.
  const detailsSubmittedKey = `detailsSubmitted_${userId}`;
  const detailsDone = !wizardMode && !!userId && localStorage.getItem(detailsSubmittedKey) === "true";
  const submittingRef = useRef(false); // guard: Continue saves only once

  // ─── Phone value handling ───
  // react-phone-input-2 blanks its display whenever the `country` prop changes
  // and ignores a `value` change in that same update. So when a number arrives
  // with a different country, set the country first and apply the digits on
  // the next render (pendingPhoneRef + the [country] effect below).
  // countryLockedRef: once a number is prefilled / picked / typed, the
  // auto-detected country (locale / IP) must not change it anymore.
  const countryLockedRef = useRef(false);
  const pendingPhoneRef = useRef(null);
  const countryRef = useRef(country);
  countryRef.current = country;
  const phoneRef = useRef(phone);
  phoneRef.current = phone;
  const isPhoneEmpty = () => !String(phoneRef.current || "").replace(/\D/g, "");

  // Saved profile / OwnerDetails number: E.164 ("+918800123456") or digits
  // carrying the dial code.
  const applyPhone = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return;
    let national = raw.replace(/\D/g, "");
    let iso = null;
    try {
      const pn = parsePhoneNumberFromString(raw.startsWith("+") ? raw : `+${raw}`);
      if (pn?.nationalNumber) {
        national = pn.nationalNumber;
        iso = pn.country ? pn.country.toLowerCase() : null;
      }
    } catch (e) {
      /* unparseable — keep the plain digits */
    }
    if (!national) return;
    countryLockedRef.current = true;
    if (iso && iso !== countryRef.current) {
      pendingPhoneRef.current = national;
      setPhone("");
      setCountry(iso);
    } else {
      setPhone(national);
    }
    setPhoneError("");
  };

  useEffect(() => {
    if (pendingPhoneRef.current === null) return;
    const pending = pendingPhoneRef.current;
    pendingPhoneRef.current = null;
    setPhone(pending);
  }, [country]);

  // Profile phone is checked first, so the device picker never opens for a
  // user who already has a number on record.
  const [profileChecked, setProfileChecked] = useState(false);

  // Device phone-number autofill (RN WebView only). Android opens Google's
  // number picker; iOS focuses the input so the keyboard suggests the number.
  // The PhoneInput here uses disableCountryCode, so its value is national
  // digits only (no dial code) — empty means zero digits. The number is NOT
  // verified and the field stays editable.
  useSimPhoneNumberAutofill({
    enabled: profileChecked && !detailsDone,
    isEmpty: isPhoneEmpty,
    // Mobile app number → number field only. The country code is left at
    // whatever the field already shows (never changed from here). If the app
    // sends "+<cc>…", only the national part goes into the field; plain
    // digits go in as-is.
    onNumber: (num) => {
      const raw = String(num || "").trim();
      let digits = raw.replace(/\D/g, "");
      if (raw.startsWith("+")) {
        try {
          const pn = parsePhoneNumberFromString(raw);
          if (pn?.nationalNumber) digits = pn.nationalNumber;
        } catch (e) {
          /* unparseable — keep the plain digits */
        }
      }
      if (!digits) return;
      countryLockedRef.current = true;
      setPhone(digits);
      setPhoneError("");
    },
    onUnsupported: () => {
      document.querySelector('input[name="phone"]')?.focus();
    },
  });

  // const fetchEmail = "false" // ← "false" hone pe email field dikhegi
  const fetchEmail = localStorage.getItem("fetchEmail"); // ← "false" hone pe email field dikhegi

  const location = useLocation();

  const handleFlagClick = () => {
    const container = phoneInputRef.current;
    if (container) {
      container.classList.add("fullscreenCountryDropdown");
    }
    const handleOutsideClick = (event) => {
      if (container && !container.contains(event.target)) {
        container.classList.remove("fullscreenCountryDropdown");
        document.removeEventListener("click", handleOutsideClick);
      }
    };
    document.addEventListener("click", handleOutsideClick);
  };

  useEffect(() => {
    const nameFromParams = searchParams.get("name");
    if (nameFromParams) {
      const decodedName = decodeURIComponent(nameFromParams);
      setName(sanitizeNameInput(decodedName));
    } else if (decodeTokenData?.name) {
      // Clover / JWT-login flow: naam JWT token mein aata hai → pre-fill.
      setName(sanitizeNameInput(decodeTokenData.name));
    }
    if (sessionStorage.getItem("OwnerDetails")) {
      const ownerDetails = JSON.parse(sessionStorage.getItem("OwnerDetails"));
      setName(sanitizeNameInput(ownerDetails.name || ""));
      // PhoneInput now uses disableCountryCode={true} so it expects only national
      // digits in the value. OwnerDetails saves E.164 ("+918800123456") — parse
      // it, derive the country (so dialCodeBox shows correctly), and feed only
      // the national part back into the input. Falls back gracefully if the
      // saved value can't be parsed (older format / partial entry).
      applyPhone(ownerDetails.phone);
    }
  }, [searchParams]);


  useEffect(() => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: "GET_LOCATION",
        email: user.email,
        // data: environmentInfo
      }));
    }
  }, []);

  /*
   * UTM attribution — on landing. OTP login already sends utm_data at
   * LoginWithEmailOTP time and sets the "utmSynced" flag. Google/Apple
   * sign-ups never send UTM at login, so we attach it here (keyed by userId)
   * the moment the user lands on this page — no longer waiting for the form
   * submit. The "utmSynced" flag prevents a duplicate write for OTP users and
   * on any re-mount of this page.
   */
  useEffect(() => {
    const syncUtmOnLanding = async () => {
      if (!userId) return;

      const utmData = JSON.parse(localStorage.getItem("utm_data") || "null");
      const shouldSendUtm =
        utmData &&
        Object.keys(utmData).length > 0 &&
        localStorage.getItem("utmSynced") !== "true";

      if (!shouldSendUtm) return;

      try {
        const response = await axios.put(
          `${API_BASE_URL}/endusers/users/${userId}`,
          { utm_data: utmData }
        );
        if (response.status === 200) {
          // UTM ab backend pe attach ho gaya — flag set so it won't re-send.
          localStorage.setItem("utmSynced", "true");
        }
      } catch (error) {
        console.error("❌ Error syncing UTM on landing:", error);
      }
    };

    syncUtmOnLanding();
  }, [userId]);


  const containsEmoji = (text) => {
    return /[\p{Emoji_Presentation}\u200d]/u.test(text);
  };

  const sanitizeNameInput = (value = "") => {
    const lettersAndSpacesOnly = value.replace(/[^A-Za-z\s]/g, "");
    const normalizedSpaces = lettersAndSpacesOnly.replace(/\s+/g, " ");
    return normalizedSpaces.replace(/^\s+/, "");
  };

  const validateName = (value) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return "Name is required.";
    if (containsEmoji(value)) return "Emojis are not allowed in the name.";
    if (!/^[A-Za-z]+(?: [A-Za-z]+)*$/.test(trimmedValue)) {
      return "Only alphabets with single spaces between words are allowed.";
    }
    if (trimmedValue.length < 2) return "Name must be at least 2 characters.";
    return "";
  };

  // ─── Email validation (compulsory jab fetchEmail === "false") ───
  const validateEmail = (value) => {
    if (!value || !value.trim()) return "Email is required.";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value.trim())) return "Please enter a valid email address.";
    return "";
  };

  const handleNameChange = (e) => {
    const val = sanitizeNameInput(e.target.value);
    setName(val);
    if (nameSubmitted) {
      setNameError(validateName(val));
    } else {
      setNameError("");
    }
  };

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setEmail(val);
    if (nameSubmitted) {
      setEmailError(validateEmail(val));
    } else {
      setEmailError("");
    }
  };

  const handleLoginClick = async () => {
    // Repeated clicks / Enter while saving (or after success) do nothing.
    if (submittingRef.current) return;
    // GA4 safety-net: if the name / phone came PREFILLED (Google/JWT login),
    // the field was never blurred so the "entered" events never fired. Fire
    // them here on Continue (source: prefilled) so they aren't lost. Skipped
    // when blur already fired them (manual typing).
    if (!nameEnteredFiredRef.current && name && name.trim()) {
      nameEnteredFiredRef.current = true;
      trackEvent("personal_name_entered", { screen: "personal_details", source: "prefilled" });
    }
    if (!phoneEnteredFiredRef.current && phone && phone.trim()) {
      phoneEnteredFiredRef.current = true;
      trackEvent("personal_phone_entered", { screen: "personal_details", country: country || "", source: "prefilled" });
    }

    // GA4: Continue pressed on Personal Details — fires at the click, before
    // validation. (personal_details_submit fires later, after the API saves.)
    trackEvent("personaldetails_entered_confirmed", {
      has_phone: !!phone,
    });
    setNameSubmitted(true);
    setPhoneSubmitted(true);

    const nError = validateName(name);
    const pError = validatePhone(phone, country);

    // ─── Email validate karo sirf jab fetchEmail === "false" ho ───
    const eError = fetchEmail === "false" ? validateEmail(email) : "";

    setNameError(nError);
    setPhoneError(pError);
    setEmailError(eError);

    if (nError) return;
    if (pError) return;
    if (eError) return; // ← email error hone pe rok do

    submittingRef.current = true;
    setLoading(true);

    /*
     * The PhoneInput now stores only the national digits (disableCountryCode=true).
     * Backend has historically received the full international form. Reconstruct
     * E.164 (e.g. "+918800123456") via libphonenumber-js, with a simple concat
     * fallback if parsing fails.
     */
    let phoneToSend = "";
    if (phone) {
      try {
        const pn = parsePhoneNumberFromString(phone, country?.toUpperCase());
        phoneToSend = pn?.format("E.164") || `${dialCodeText}${phone}`;
      } catch (err) {
        phoneToSend = `${dialCodeText}${phone}`;
      }
    }

    /*
     * UTM attribution ab landing pe sync hoti hai (upar wali useEffect dekho),
     * isliye yahan submit ke time bhejne ki zaroorat nahi.
     */
    try {
      const localDateTime = new Date().toISOString();
      const response = await axios.put(
        `${API_BASE_URL}/endusers/users/${userId}`,
        {
          name: name.trim(),
          phone: phoneToSend,
          // ─── email sirf tab bhejo jab fetchEmail === "false" ho ───
          ...(fetchEmail === "false" && { email: email.trim() }),
          referredBy: referralCode || "",
          referredByName: referredByName || "",
          referredOn: localDateTime,
          userType: 0,
          verifyDetails: true,
          // ─── SMS promotional + xdffffftransactional consent ───
          smsConsent: smsConsent,
          ...(smsConsent && {
            smsConsentAt: new Date().toISOString(),
            /*
             * Consent ON hai to teeno per-event alerts bhi ON jaate hain — user
             * inhe baad me Settings se alag-alag band kar sakta hai.
             * Consent OFF par ye bheja hi nahi jaata: canSendSms() waise bhi
             * consent par gate karta hai, aur na bhejne se user ki purani
             * per-event choices bachi rehti hain (wapas opt-in kare to wahi
             * milengi).
             */
            smsPrefs: {
              newLead: true,
              meetingBooked: true,
              transferFailed: true,
            },
          }),
        }
      );

      if (response.status === 200) {
        trackEvent("personal_details_submit", {
          has_phone: !!phoneToSend,
          sms_opt_in: !!smsConsent,
        });
        setStartExit(true);
        if (userId) localStorage.setItem(detailsSubmittedKey, "true");
        sessionStorage.setItem(
          "OwnerDetails",
          JSON.stringify({ name: name.trim(), phone: phoneToSend, smsConsent })
        );
        // Partial merge so persisted profile URL isn't dropped.
        updateUser({ name: name });
        setTimeout(() => {
          // Every path from here goes to the business category step; the hook
          // owns that (and the wizard-mode hand-off).
          goToNextStep({ wizardMode, onWizardNext });
        }, 400);

        const resolvedEmail =
          fetchEmail === "false"
            ? email.trim()
            : userDetails?.email ||
            sessionStorage.getItem("jobberIntegrationEmail") ||
            localStorage.getItem("userEmail");

        if (!resolvedEmail) {
          console.error("❌ Email missing");
          return;
        }

        // Owner email — fresh token from localStorage (apiStore's `token` is
        // captured at page load and is empty right after a fresh sign-in), and
        // a per-user guard so a re-submit never sends it twice.
        const emailSentKey = `ownerEmailSent_${userId}`;
        if (
          process.env.REACT_APP_ENV === "production" &&
          localStorage.getItem(emailSentKey) !== "true"
        ) {
          localStorage.setItem(emailSentKey, "true");
          try {
            await axios.post(
              `${API_BASE_URL}/endusers/sendEmailToOwner`,
              { email: resolvedEmail, name: name.trim(), phone: phoneToSend },
              { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
            );
          } catch (emailErr) {
            console.error("Details: sendEmailToOwner failed:", emailErr?.message);
          }
        }

        trackStep({
          stepName: "personal_details_entered",
          screenName: "Personal Details",
          email: resolvedEmail,
          userId: decodeToken(localStorage.getItem("token"))?.id || null,
          data: { name: name.trim(), phone: phoneToSend },
        });
      } else {
        setPopupType("failed");
        submittingRef.current = false;
        setPopupMessage("Update failed. Please try again.");
        setShowPopup(true);
      }
    } catch (error) {
      setPopupType("failed");
      submittingRef.current = false;
      setPopupMessage("An error occurred during update.");
      setShowPopup(true);
    } finally {
      setLoading(false);
    }
  };

  const [step, setStep] = useState(0);

  useEffect(() => {
    const played = sessionStorage.getItem("personalDetailsAnimationPlayed");
    if (!played) {
      const delays = [150, 300, 450, 550];
      const timers = delays.map((delay, index) =>
        setTimeout(() => setStep(index + 1), delay)
      );
      sessionStorage.setItem("personalDetailsAnimationPlayed", "true");
      return () => timers.forEach(clearTimeout);
    } else {
      setStep(4);
    }
  }, []);

  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handlePopState = (e) => {
      window.history.pushState(null, "", window.location.href);
      const confirmExit = window.confirm(
        "Are you sure you want to leave? You might lose unsaved changes."
      );
      if (!confirmExit) {
        window.close();
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Country code defaults to +91 on the .in site and +1 otherwise — no locale
  // / IP auto-detect. The user can change it from the dropdown; a saved E.164
  // number sets its own country.

  /*
   * Phone validation. fdfWith disableCountryCode={true} the library returns only the
   * national digits (no dial code prefix), so we pass the ISO country (e.g. "IN")
   * to libphonenumber-js and let it validate against that numbering plan.
   */
  const validatePhone = (value, countryISO) => {
    if (!value || value.trim() === "") return "Phone number is required.";
    try {
      const iso = (countryISO || country || "us").toUpperCase();
      const phoneNumber = parsePhoneNumberFromString(value, iso);
      if (!phoneNumber || !phoneNumber.nationalNumber) return "Invalid phone number.";
      if (!phoneNumber.isValid()) return "Invalid number for selected country.";
      return "";
    } catch (error) {
      return "Invalid phone number.";
    }
  };

  // Dial code text shown in the visual prefix box (Figma: "+91 | 88XX...").
  // Falls back to "+1" if the country state isn't yet a valid ISO code.
  let dialCodeText = "+1";
  try {
    if (country) {
      dialCodeText = `+${getCountryCallingCode(country.toUpperCase())}`;
    }
  } catch (e) {
    dialCodeText = "+1";
  }

  /*
   * Trigger react-phone-input-2's hidden country dropdown by programmatically
   * clicking its .selected-flag button. We hide that button visually but keep
   * it in DOM as the dropdown trigger. Our own .dialCodeBox forwards clicks here.
   */
  const phoneFieldWrapperRef = useRef(null);
  const handleDialCodeClick = () => {
    const flagBtn = phoneFieldWrapperRef.current?.querySelector(".selected-flag");
    if (flagBtn) flagBtn.click();
  };

  const scrollListIntoView = () => {
    if (listRef.current) {
      listRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const fetchUserDetails = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/auth/getUserDetails`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const user = res.data.user;
      // Partial merge so persisted profile URL isn't dropped.
      updateUser({ name: user.name });
      setUserDetails(user);
    } catch (err) {
      console.error("❌ Error fetching user details:", err);
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, []);

  // Fetch the user's location (city / state / pinCode / country) from the
  // enduser record and stash it locally BEFORE the user reaches BusinessStep.
  // BusinessStep's Zip / Postal Code field uses the country to pick the right
  // keyboard (India / US → numeric, Canada → alphanumeric).
  useEffect(() => {
    if (!userId) {
      setProfileChecked(true);
      return;
    }
    (async () => {
      try {
        const data = await getUserDetails(userId, token);
        const u = data?.user || data || {};
        // Saved phone from the profile → prefill (only if the field is empty).
        if (u.phone && isPhoneEmpty()) applyPhone(u.phone);
        const loc = {
          city: u.city || "",
          state: u.state || "",
          pinCode: u.pinCode || "",
          country: u.country || "",
        };
        if (loc.city || loc.state || loc.pinCode || loc.country) {
          sessionStorage.setItem("userLocation", JSON.stringify(loc));
          if (loc.country) sessionStorage.setItem("userCountry", loc.country);
        }
      } catch (err) {
        console.error("❌ Error fetching user location:", err);
      } finally {
        setProfileChecked(true);
      }
    })();
  }, [userId, token]);

  useEffect(() => {
    if (userDetails?.name) {
      setName(sanitizeNameInput(userDetails.name));
    }
    if (userDetails?.smsConsent !== undefined) {
      setSmsConsent(userDetails.smsConsent);
    }
    // Server says details are already done (e.g. localStorage was cleared).
    const v = userDetails?.verifyDetails;
    if (!wizardMode && (v === true || v === 1 || v === "true")) {
      localStorage.setItem("onboardComplete", "true");
      if (userId) localStorage.setItem(detailsSubmittedKey, "true");
      const d = decodeTokenData || decodeToken(token);
      resolveHomeRoute(d).then((path) => navigate(path, { replace: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails]);

  // Already submitted → route cleanly to destination based on agent count.
  useEffect(() => {
    if (detailsDone) {
      const d = decodeTokenData || decodeToken(token);
      resolveHomeRoute(d).then((path) => navigate(path, { replace: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsDone]);

  if (detailsDone) return null;

  return (
    <>
      <div className={styles.signUpContainer}>
        <div className={styles.pageWrapper}>
          <div className={`${styles.mask} ${styles.maskZoomFadeIn}`}>
            <img src="svg/Rexpt-Logo.svg" alt="Mask" />
          </div>
          <div className={styles.logimg2}></div>

          <div
            className={`${styles.Maincontent} ${styles.animateStep} ${step >= 2 ? styles.animateStep2 : ""
              }`}
          >
            <div className={styles.welcomeTitle}>
              <h1>Personal Details</h1>
            </div>
          </div>

          <div
            className={`${styles.container} ${styles.animateStep} ${step >= 3 ? styles.animateStep3 : ""
              }`}
          >


            {/* ─── Name Field ─── */}
            <div className={styles.labReq}>
              <div className={styles.Dblock}>
                <label className={styles.label}>Name</label>
                <input
                  type="text"
                  className={`${styles.input} ${nameError ? styles.inputError : ""}`}
                  placeholder="Your name"
                  maxLength={150}
                  value={name}
                  onChange={handleNameChange}
                  onBlur={(e) => {
                    // GA4: user finished TYPING their name (left the field
                    // non-empty). The name itself is PII — never sent to GA.
                    if (e.target.value.trim()) {
                      nameEnteredFiredRef.current = true;
                      trackEvent("personal_name_entered", { screen: "personal_details", source: "typed" });
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !loading) handleLoginClick();
                  }}
                  onFocus={(e) => {
                    if (!isIphone) setTimeout(scrollListIntoView, 300);
                  }}
                  onClick={() => {
                    if (!isIphone) setTimeout(scrollListIntoView, 300);
                  }}
                />
              </div>
              {nameError && <p className={styles.inlineError}>{nameError}</p>}
            </div>

            {/* ─── Phone Field ─── */}
            <div className={styles.labReq} ref={listRef}>
              <div className={styles.Dblock}>
                <label className={styles.label}>
                  Mobile Number <span className={styles.labelOptional}>(for SMS call alerts)</span>
                </label>
                <div
                  ref={phoneFieldWrapperRef}
                  className={`${styles.phoneFieldRow} ${phoneError ? styles.inputError : ""}`}
                >
                  <button
                    type="button"
                    className={styles.dialCodeBox}
                    onClick={handleDialCodeClick}
                    aria-label="Change country"
                  >
                    <span className={styles.dialCodeText}>{dialCodeText}</span>
                    <span className={styles.dialCodeCaret} aria-hidden="true">▾</span>
                  </button>
                  <div className={styles.phoneFieldNoFlag}>
                    <PhoneInput
                      ref={phoneInputRef}
                      country={country}
                      enableSearch={true}
                      disableCountryCode={true}
                      disableCountryGuess={true}
                      disableInitialCountryGuess={true}
                      value={phone}
                      onChange={(val, countryData) => {
                        // User touched the field — locale / IP detection must
                        // not change the country anymore.
                        countryLockedRef.current = true;

                        // With guess disabled, this branch only fires when the user
                        // explicitly picks a country from the dropdown — keep our
                        // country state in sync so dialCodeText updates. The
                        // library blanks its display on a country change, so the
                        // digits are re-applied once the country settles.
                        let nextPhone = val;

                        if (
                          countryData?.countryCode &&
                          countryData.countryCode !== country
                        ) {
                          /*
                           * Country dropdown se badalne par library `val` me naye
                           * desh ka DIAL CODE bhej deti hai (Andorra chunne par
                           * "376"), user ka number nahi. Pehle hum wahi val wapas
                           * number field me likh dete the, isliye input me "376"
                           * dikhne lagta tha. Ab dial code prefix hata dete hain,
                           * aur kuch na bache to user ke pehle se type kiye digits
                           * hi rakhte hain (country badalne se number nahi jaana
                           * chahiye).
                           */
                          const dialCode = String(countryData.dialCode || "").replace(/\D/g, "");
                          const valDigits = String(val || "").replace(/\D/g, "");
                          const typedDigits = String(phoneRef.current || "").replace(/\D/g, "");
                          const withoutDialCode =
                            dialCode && valDigits.startsWith(dialCode)
                              ? valDigits.slice(dialCode.length)
                              : valDigits;

                          nextPhone = withoutDialCode || typedDigits;

                          if (nextPhone) {
                            pendingPhoneRef.current = nextPhone;
                            setPhone("");
                          } else {
                            setPhone("");
                          }
                          setCountry(countryData.countryCode);
                        } else {
                          setPhone(val);
                        }

                        if (phoneSubmitted) {
                          setPhoneError(
                            validatePhone(nextPhone, countryData?.countryCode || country)
                          );
                        } else {
                          setPhoneError("");
                        }
                      }}
                      onClickFlag={handleFlagClick}
                      onBlur={() => {
                        // GA4: user finished TYPING their mobile number (left the
                        // field non-empty). The number itself is PII — only the
                        // country goes to GA, never the number.
                        if (phone && phone.trim()) {
                          phoneEnteredFiredRef.current = true;
                          trackEvent("personal_phone_entered", {
                            screen: "personal_details",
                            country: country || "",
                            source: "typed",
                          });
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !loading) handleLoginClick();
                      }}
                      inputClass={styles.phoneInput}
                      inputProps={{
                        name: "phone",
                        type: "tel",
                        autoComplete: "tel",
                        inputMode: "tel",
                      }}
                      placeholder="88XX XXX 5XX"
                    />
                  </div>
                </div>
              </div>
              {phoneError && <p className={styles.inlineError}>{phoneError}</p>}
            </div>

          </div>

          <div className={styles.smsConsentRow}>
            <label className={styles.toggleSwitch}>
              <input
                type="checkbox"
                checked={smsConsent}
                onChange={(e) => {
                  setSmsConsent(e.target.checked);
                  // GA4: user toggled the SMS promotional/alerts consent.
                  // opted_in tells which way they flipped it.
                  trackEvent("sms_consent_toggled", {
                    screen: "personal_details",
                    opted_in: e.target.checked,
                  });
                }}
              />
              <span className={styles.toggleSlider}></span>
            </label>
            <p className={styles.smsConsentText}>
              I want to receive promotional offers and transactional system
              alerts from Rexpt via SMS. Message and data rates may apply.
              Message frequency varies. Reply STOP to cancel.
            </p>
          </div>

          {/* ─── Email Field — sirf tab dikhega jab fetchEmail === "false" ─── */}
          {fetchEmail === "false" && (
            <div className={styles.labReq}>
              <div className={styles.Dblock}>
                <label className={styles.label}>Email</label>
                <input
                  type="email"
                  className={`${styles.input} ${emailError ? styles.inputError : ""}`}
                  placeholder="Enter your Email"
                  maxLength={150}
                  value={email}
                  onChange={handleEmailChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !loading) handleLoginClick();
                  }}
                  onFocus={() => {
                    if (!isIphone) setTimeout(scrollListIntoView, 300);
                  }}
                  onClick={() => {
                    if (!isIphone) setTimeout(scrollListIntoView, 300);
                  }}
                />
              </div>
              {emailError && <p className={styles.inlineError}>{emailError}</p>}
            </div>
          )}

          {/* SMS alerts tip line (Figma) */}
          <p className={styles.smsTip}>
            <span className={styles.smsTipIcon} aria-hidden="true"><img src="/svg/bulb_icon.svg" /></span>
            We'll send you a text when your AI receptionist answers a call for your business.
          </p>
        </div>

        <div
          className={`${styles.Btn} ${styles.animateStep} ${step >= 4 ? styles.animateStep4 : ""
            }`}
          onClick={handleLoginClick}
        >
          <div type="submit">
            <div className={styles.btnTheme}>
              <AnimatedButton
                isLoading={loading}
                label="Continue"
                bottom="40px"
              />
            </div>
          </div>
        </div>

        {showPopup && (
          <PopUp
            type={popupType}
            onClose={() => setShowPopup(false)}
            message={popupMessage}
          />
        )}
      </div>
    </>
  );
};

export default Details;
